import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Tabs } from '@/components/ui-kit/tabs';
import {
  ContractsTabsList,
  type ContractsTabsListOption,
} from '@/modules/org/contracts/components/contracts-tabs-list';

import { cn } from '@/lib/utils';
import { Plus, RefreshCw, ShieldCheck, UserCog, Users } from 'lucide-react';

import type { UserRole } from '@/state/store/auth/index.type';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  useGetAdminUsers,
  useUpdateAdminUserRole,
  useRemoveAdminUser,
  useInviteAdminUser,
} from '../hooks/use-admin-users';
import { useAdminUsersUiStore } from '../store/use-admin-users-ui-store';
import { DeleteUserDialog } from './delete-user-dialog';
import { EditRoleDialog } from './edit-role-dialog';
import { InviteDialog } from './invite-dialog';
import { UsersList, type RoleFilter } from './users-list';
import type { AdminUser } from '../types/admin-users.types';

export type SerializedUser = AdminUser;

export function UsersAdminClient() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Data fetching via hooks
  const { data, isLoading } = useGetAdminUsers();
  const users = data?.users || [];

  // Mutations
  const updateRoleMutation = useUpdateAdminUserRole();
  const removeMutation = useRemoveAdminUser();
  const inviteMutation = useInviteAdminUser();

  // UI state
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();

  // Dialog states. The invite dialog open-state lives in Zustand so the
  // AdminTopbar can open it on mobile without prop drilling through
  // AdminShell.
  const inviteOpen = useAdminUsersUiStore((s) => s.inviteDialogOpen);
  const setInviteOpen = useAdminUsersUiStore((s) => s.setInviteDialogOpen);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [pendingRoleChange, setPendingRoleChange] = useState<{
    userId: string;
    newRole: UserRole;
    currentRole: UserRole | null;
  } | null>(null);

  // Refresh all data
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
    setIsRefreshing(false);
    toast({ variant: 'success', title: t('ADMIN_USERS_TOASTS_DATAREFRESHED') });
  }, [queryClient, t, toast]);

  // Handle inline role change with optimistic UI
  const handleRoleChange = async (
    userId: string,
    newRole: UserRole,
    currentRole: UserRole | null
  ) => {
    // If demoting an admin, show confirmation dialog
    if (currentRole === 'sys_admin' && newRole !== 'sys_admin') {
      const user = users.find((u) => u.id === userId);
      if (user) {
        setSelectedUser(user);
        setPendingRoleChange({ userId, newRole, currentRole });
        setEditRoleOpen(true);
        return;
      }
    }

    try {
      await updateRoleMutation.mutateAsync({ userId, role: newRole });
    } catch {
      // Error handled by hook
    }
  };

  // Handle edit role dialog confirmation (for admin demotion)
  const handleConfirmEditRole = async (newRole: UserRole) => {
    if (!pendingRoleChange) return;

    await updateRoleMutation.mutateAsync({
      userId: pendingRoleChange.userId,
      role: newRole,
    });

    setSelectedUser(null);
    setPendingRoleChange(null);
  };

  // Handle delete
  const handleOpenDelete = (user: AdminUser) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    await removeMutation.mutateAsync(selectedUser.id);
    setSelectedUser(null);
  };

  // Handle invite
  const handleConfirmInvite = async (
    email: string,
    firstName: string,
    lastName: string,
    role: UserRole
  ) => {
    await inviteMutation.mutateAsync({ email, firstName, lastName, role });
  };

  // Toggle filter on stats card click
  const toggleFilter = (filter: RoleFilter) => {
    setRoleFilter((prev) => (prev === filter ? 'all' : filter));
  };

  // Stats
  const adminCount = users.filter((u) => u.role === 'sys_admin').length;
  const editorCount = users.filter((u) => u.role === 'sys_editor').length;
  const noRoleCount = users.filter((u) => !u.role).length;
  const totalCount = users.length;

  // Refresh button reused inside UsersList's search toolbar on mobile.
  const refreshButton = (
    <Button
      variant="outline"
      size="icon"
      onClick={handleRefresh}
      disabled={isRefreshing}
      className="shrink-0"
      aria-label={t('ADMIN_USERS_ACTIONS_REFRESH')}
    >
      <RefreshCw className={`size-4 ${isRefreshing ? 'animate-spin' : ''}`} />
    </Button>
  );

  // Filter tabs — same shape contracts page uses. Inline counts via the
  // `badge` slot so the active count is always visible.
  const filterTabs: ReadonlyArray<ContractsTabsListOption<RoleFilter>> = [
    {
      value: 'all',
      label: t('ADMIN_USERS_FILTERS_ALL', { count: totalCount }),
    },
    {
      value: 'sys_admin',
      label: t('ADMIN_USERS_FILTERS_ADMINS', { count: adminCount }),
    },
    {
      value: 'sys_editor',
      label: t('ADMIN_USERS_FILTERS_EDITORS', { count: editorCount }),
    },
    {
      value: 'none',
      label: t('ADMIN_USERS_FILTERS_NOROLE', { count: noRoleCount }),
    },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header — actions are md+ only; on mobile the Invite button lives
          in the AdminTopbar and Refresh sits beside the search bar. */}
      <div className="flex flex-col gap-3 md:gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold text-[hsl(var(--fg))] font-heading">
            {t('ADMIN_USERS_TITLE')}
          </h1>
          <p className="text-sm md:text-base text-globe-grey mt-1">
            {t('ADMIN_USERS_DESCRIPTION')}
          </p>
        </div>
        <div className="hidden md:flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={`size-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {t('ADMIN_USERS_ACTIONS_REFRESH')}
          </Button>
          <Button onClick={() => setInviteOpen(true)} size="sm">
            <Plus className="size-4" />
            {t('ADMIN_USERS_ACTIONS_INVITE')}
          </Button>
        </div>
      </div>

      {/* Stats Cards — always 3 in a row, compact on mobile (icon hidden,
          padding + numbers shrink). Click to toggle filter. */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card
          className={cn(
            'cursor-pointer transition-all hover:shadow-md',
            roleFilter === 'sys_admin' && 'ring-2 ring-selise-blue bg-selise-blue/5'
          )}
          onClick={() => toggleFilter('sys_admin')}
        >
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:flex size-12 items-center justify-center rounded-lg bg-selise-blue/10">
                <ShieldCheck className="size-6 text-selise-blue" />
              </div>
              <div className="min-w-0">
                <div className="text-lg sm:text-2xl font-bold text-[hsl(var(--fg))]">
                  {isLoading ? <Skeleton className="h-6 w-10 sm:h-8 sm:w-12" /> : adminCount}
                </div>
                <p className="text-xs sm:text-sm text-globe-grey truncate">
                  {t('ADMIN_USERS_STATS_ADMINS')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            'cursor-pointer transition-all hover:shadow-md',
            roleFilter === 'sys_editor' && 'ring-2 ring-lime-green bg-lime-green/5'
          )}
          onClick={() => toggleFilter('sys_editor')}
        >
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:flex size-12 items-center justify-center rounded-lg bg-lime-green/10">
                <UserCog className="size-6 text-lime-green" />
              </div>
              <div className="min-w-0">
                <div className="text-lg sm:text-2xl font-bold text-[hsl(var(--fg))]">
                  {isLoading ? <Skeleton className="h-6 w-10 sm:h-8 sm:w-12" /> : editorCount}
                </div>
                <p className="text-xs sm:text-sm text-globe-grey truncate">
                  {t('ADMIN_USERS_STATS_EDITORS')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            'cursor-pointer transition-all hover:shadow-md',
            roleFilter === 'none' && 'ring-2 ring-globe-grey bg-globe-grey/5'
          )}
          onClick={() => toggleFilter('none')}
        >
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:flex size-12 items-center justify-center rounded-lg bg-globe-grey/10">
                <Users className="size-6 text-globe-grey" />
              </div>
              <div className="min-w-0">
                <div className="text-lg sm:text-2xl font-bold text-[hsl(var(--fg))]">
                  {isLoading ? <Skeleton className="h-6 w-10 sm:h-8 sm:w-12" /> : noRoleCount}
                </div>
                <p className="text-xs sm:text-sm text-globe-grey truncate">
                  {t('ADMIN_USERS_STATS_NOROLE')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter tabs — like the contracts page. First 2 inline on mobile,
          rest collapse into a "More" dropdown. */}
      <Tabs value={roleFilter} onValueChange={(v) => setRoleFilter(v as RoleFilter)}>
        <ContractsTabsList<RoleFilter>
          options={filterTabs}
          activeValue={roleFilter}
          onSelect={setRoleFilter}
          visibleCount={4}
        />
      </Tabs>

      {/* Users List */}
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="p-4">
                <div className="flex items-start gap-3 mb-4">
                  <Skeleton className="size-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-9 flex-1" />
                  <Skeleton className="size-9" />
                </div>
                <Skeleton className="h-3 w-24 mt-3" />
              </Card>
            ))}
          </div>
        </div>
      ) : (
        <UsersList
          users={users}
          roleFilter={roleFilter}
          onRoleChange={handleRoleChange}
          onDelete={handleOpenDelete}
          onInvite={() => setInviteOpen(true)}
          searchToolbarAction={refreshButton}
        />
      )}

      {/* Dialogs */}
      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onConfirm={handleConfirmInvite}
      />

      <EditRoleDialog
        open={editRoleOpen}
        onOpenChange={(open) => {
          setEditRoleOpen(open);
          if (!open) {
            setPendingRoleChange(null);
          }
        }}
        user={selectedUser}
        pendingRole={pendingRoleChange?.newRole || null}
        onConfirm={handleConfirmEditRole}
      />

      <DeleteUserDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        user={selectedUser}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
